import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Phone, Link as LinkIcon, ExternalLink } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import { TESTIDS } from "@/constants/testIds";

export default function OffersTab({ projectId, onRefresh }) {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ name: "", cta_type: "phone", phone_number: "", target_url: "", service: "" });
  const [busy, setBusy] = useState(false);

  async function load() {
    try { const { data } = await api.get("/offers", { params: { project_id: projectId } }); setOffers(data); }
    catch { toast.error("Failed to load offers"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  async function create() {
    if (!form.name) return toast.error("Offer name required");
    setBusy(true);
    try {
      await api.post(`/offers`, { ...form, active: true }, { params: { project_id: projectId } });
      toast.success("Offer created");
      setForm({ name: "", cta_type: "phone", phone_number: "", target_url: "", service: "" });
      load(); onRefresh?.();
    } catch (err) { toast.error(err?.response?.data?.detail || "Offer create failed"); }
    finally { setBusy(false); }
  }

  async function del(id) {
    await api.delete(`/offers/${id}`); load(); onRefresh?.();
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <section className="lg:col-span-2 border border-border overflow-x-auto">
        <table className="w-full text-xs" data-testid={TESTIDS.offersTable}>
          <thead>
            <tr className="bg-secondary border-b border-border text-left">
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Name</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Type</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Target</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Service</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Slug</th>
              <th className="px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground txt-mono">Status</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {offers.map((o) => (
              <tr key={o.id} className="border-b border-border last:border-b-0">
                <td className="px-3 py-2 txt-mono">{o.name}</td>
                <td className="px-3 py-2">
                  {o.cta_type === "phone" ? <Phone className="w-3 h-3 inline mr-1" /> : <LinkIcon className="w-3 h-3 inline mr-1" />}
                  <span className="txt-mono">{o.cta_type}</span>
                </td>
                <td className="px-3 py-2 txt-mono text-muted-foreground max-w-xs truncate">{o.phone_number || o.target_url || "—"}</td>
                <td className="px-3 py-2 txt-mono">{o.service || "—"}</td>
                <td className="px-3 py-2 txt-mono text-muted-foreground">/go/{o.slug}/</td>
                <td className="px-3 py-2"><StatusPill status={o.active ? "active" : "draft"} /></td>
                <td className="px-3 py-2 text-right">
                  {o.cta_type === "affiliate" && (
                    <a href={`/api/go/${o.slug}/`} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary mr-2 inline-block">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  <button onClick={() => del(o.id)} className="text-muted-foreground hover:text-red-400"><Trash2 className="w-3 h-3 inline" /></button>
                </td>
              </tr>
            ))}
            {offers.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground text-xs">No offers yet. Create your first Pay-Per-Call or affiliate offer →</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <section className="border border-border p-4">
        <h3 className="text-sm font-semibold mb-3">Create offer</h3>
        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Name</div>
            <input data-testid={TESTIDS.offerNameInput} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Call 24/7 Water Damage"
              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono" />
          </label>
          <label className="block">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">CTA type</div>
            <select data-testid={TESTIDS.offerTypeSelect} value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value })}
              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono">
              <option value="phone">Phone (Pay-Per-Call)</option>
              <option value="affiliate">Affiliate (cloaked /go/ link)</option>
              <option value="quote_form">Quote form</option>
            </select>
          </label>
          {form.cta_type === "phone" ? (
            <label className="block">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Phone (with country code)</div>
              <input data-testid={TESTIDS.offerPhoneInput} value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+18005551234"
                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono" />
            </label>
          ) : (
            <label className="block">
              <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Target URL</div>
              <input data-testid={TESTIDS.offerUrlInput} value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://provider.com/?ref=…"
                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono" />
            </label>
          )}
          <label className="block">
            <div className="text-[10px] txt-mono uppercase text-muted-foreground mb-1">Service (optional, restricts CTA to a service)</div>
            <input data-testid={TESTIDS.offerServiceInput} value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="water-damage"
              className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm rounded-sm txt-mono" />
          </label>
          <button
            data-testid={TESTIDS.offerCreateBtn}
            onClick={create} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-3 py-2 text-sm font-medium rounded-sm transition-colors"
          >
            <Plus className="w-3 h-3" /> Create offer
          </button>
        </div>
      </section>
    </div>
  );
}
